/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';

import { Box, DialogTitle, Grid, IconButton, LinearProgress } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import CheckCircle from '@material-ui/icons/CheckCircle';
import { CancelRounded } from '@material-ui/icons';
import Button from '../../../../components/common/Button';

const useStyles = makeStyles(theme => ({
  ...theme.customTheme,
  buttonGroup: {
    marginTop: '2rem',
    '& button:first-child': {
      marginRight: '1rem',
    },
  },
  paper: {
    borderRadius: '0px',
    minWidth: '25%',
    maxHeight: '421px',
    maxWidth: '429px',
    overflowY: 'scroll',
    position: 'absolute',
    top: '0px',
    right: '0px',
    paddingTop: '0.1rem',
    paddingLeft: '1.2rem',
    paddingRight: '1.2rem',
  },
  dialogTitle: {
    width: '100%',
    paddingBottom: '1.2rem',
    padding: '0',
  },
  sentBox: {
    justifyContent: 'space-between',
    width: '100%',
  },
  container: {
    [theme.breakpoints.down('sm')]: {
      padding: '1.25rem',
    },
  },
  gridRoot: {
    flexGrow: 1,
  },
  dFlexColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  dFlexRow: {
    display: 'flex',
    alignItems: 'center',
  },
  w100: {
    width: '100%',
  },
  mr: {
    marginRight: '0.7rem',
  },
  mlAuto: {
    marginLeft: 'auto',
  },
  capitalize: {
    textTransform: 'capitalize',
  },
  green: {
    color: theme.palette.completed,
  },
  red: {
    color: theme.palette.failed,
  },
  submissionProgressId: {
    marginLeft: '0.5px',
    fontWeight: '900',
  },
}));

function SentSubmissionProgressDialog({ sendSubmissionProcessDialog, sendSubmissionRequestProgress, sentSubmissions }) {
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <>
      <Dialog
        hideBackdrop
        disableEnforceFocus
        disableBackdropClick
        open={sendSubmissionProcessDialog.isOpen}
        maxWidth="xl"
        classes={{ paper: classes.paper }}
        style={{
          position: 'absolute',
          left: 10,
          top: 50,
        }}
      >
        <DialogTitle classes={{ root: classes.dialogTitle }}>
          <Box display="flex" alignItems="center" className={classes.sentBox}>
            <Box flexGrow={1}>{t('actions.status.sending')}</Box>
            <Box>
              <IconButton data-testid="close-icon" onClick={() => sendSubmissionProcessDialog.close()}>
                <CancelRounded />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <Box p={1} className={classes.container}>
          <div className={classes.gridRoot}>
            <Grid container spacing={3}>
              {sendSubmissionRequestProgress?.map(item => (
                <Grid item xs={12} key={item.submission.id} data-testid="sent-submission">
                  <div className={classes.dFlexColumn}>
                    <span>
                      {t('userReference')}
                      :
                      <span className={classes.submissionProgressId}>
                        {item.submission?.['basicFilingInfo.userReference']}
                      </span>
                    </span>

                    <div className={classes.dFlexRow}>
                      <LinearProgress
                        variant="determinate"
                        value={item.success ? 100 : 0}
                        className={clsx(classes.w100, classes.mr)}
                      />

                      {item.success && <CheckCircle className={clsx(classes.green, classes.mr)} />}
                      {!item.success && <CancelRounded className={clsx(classes.red, classes.mr)} />}

                      <p
                        className={clsx(
                          classes.mr,
                          classes.mlAuto,
                          classes.capitalize,
                          item.success ? classes.green : classes.red,
                        )}
                      >
                        {item.success ? t('sent') : t('error')}
                      </p>

                      {!item.success && (
                        <Button
                          id="retry"
                          color="primary"
                          onClick={() => sendSubmissionProcessDialog.retry(item.submission)}
                        >
                          {t('actions.retry')}
                        </Button>
                      )}
                    </div>
                  </div>
                </Grid>
              ))}
            </Grid>
          </div>

          {sendSubmissionProcessDialog.getSendSubmissionRequestErrors?.length > 1 && (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              paddingTop="1rem"
              className={classes.buttonGroup}
            >
              <span className={classes.red}>
                {`${sendSubmissionProcessDialog.getSendSubmissionRequestErrors?.length} errors found`}
              </span>

              <Button id="retry-all" color="primary" onClick={() => sendSubmissionProcessDialog.retry(sentSubmissions)}>
                {t('actions.retry.all')}
              </Button>
            </Box>
          )}
        </Box>
      </Dialog>
    </>
  );
}

export default SentSubmissionProgressDialog;
